import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import { EstadoTablasMirrorResponse } from "../../../interfaces/dashboardTypes";
import { getParametrosBuilder } from "../AsyncThunks/parametros/getParametros";
import {
  getValidacionesBuilder,
  Validacion,
} from "../AsyncThunks/validaciones/getValidaciones";
import { getEstadoTablasMirrorBuilder } from "../AsyncThunks/estadoGeneral/getEstadoTablasMirror";
import { getDetalleDeCupoBuilder } from "../AsyncThunks/cupos/getDetalleDeCupo";
import { getQueuesBuilder } from "../AsyncThunks/colas/getQueues";
import { getServicesVersionsBuilder } from "../AsyncThunks/servicios/getServicesStatus";
import { getDetalleCuadraturaDeCupoBuilder } from "../AsyncThunks/cupos/getDetalleCuadratura";
import { ICursoDetalle, IDetalleCupo } from "../../../types";
import { getDetalleDeCursoBuilder } from "../AsyncThunks/cursos/getDetalleCurso";
import { validateTokenBuilder } from "../AsyncThunks/estadoGeneral/validateToken";

export interface DashboardInitState {
  loadingPageScreen: boolean;
  sideBarOpen: boolean;
  estadoTablasMirror: EstadoTablasMirrorResponse;
  detalleCupo: IDetalleCupo | null;
  detalleCurso: ICursoDetalle | null;
  detalleCuadraturaCupos: [];
  cuposTotales: number;
  cuposAnalizados: number;
  reservasEnTransito: any[];
  desinscripcionesEnTransito: any[];
  parametros: { clave: string; valor: string; descripcion: string }[];
  validaciones: Validacion[];
  filtrarNovedadesConErrores: boolean;
  queuesData: any[];
  servicesVersions: {
    name: string;
    version: string;
    environment: string;
    category: string;
    status: "loading" | "ok" | "error";
  }[];
  tokenValidationStatus: "pending" | "valid" | "invalid" | null;
}
const initState: DashboardInitState = {
  loadingPageScreen: false,
  sideBarOpen: true,
  estadoTablasMirror: [],
  detalleCupo: null,
  detalleCurso: null,
  detalleCuadraturaCupos: [],
  reservasEnTransito: [],
  desinscripcionesEnTransito: [],
  parametros: [],
  validaciones: [],
  queuesData: [],
  servicesVersions: [],
  cuposTotales: 0,
  cuposAnalizados: 0,
  filtrarNovedadesConErrores: false,
  tokenValidationStatus: null,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: initState,
  reducers: {
    toggleSidebar: (state) => {
      state.sideBarOpen = !state.sideBarOpen;
    },
    setLoadingPageScreen: (state, { payload }) => {
      state.loadingPageScreen = payload;
    },
    setFiltrarNovedadesConErrores: (state, { payload }) => {
      state.filtrarNovedadesConErrores = payload;
    },
    setEstadoTablasMirror: (state, { payload }) => {
      state.estadoTablasMirror = payload;
    },
    patchEstadoTablaMirror: (state, { payload }) => {
      const idx = state.estadoTablasMirror.findIndex(
        (t) => t.clave === payload.clave
      );
      if (idx >= 0) {
        state.estadoTablasMirror[idx] = {
          ...state.estadoTablasMirror[idx],
          ...payload,
        };
      }
    },
    setCleanDetalleCurso: (state) => {
      state.detalleCurso = null;
    },
    setCleanDetalleCupo: (state) => {
      state.detalleCupo = null;
    },
  },
  extraReducers: (builder) => {
    getEstadoTablasMirrorBuilder(builder);
    getParametrosBuilder(builder);
    getDetalleDeCupoBuilder(builder);
    getDetalleDeCursoBuilder(builder);
    getValidacionesBuilder(builder);
    getQueuesBuilder(builder);
    getServicesVersionsBuilder(builder);
    getDetalleCuadraturaDeCupoBuilder(builder);
    validateTokenBuilder(builder);
  },
});

/* -------------------------------------------------------------------------- */
/*                                    CURSO                                   */
/* -------------------------------------------------------------------------- */
export const selectDetalleCurso = (state: RootState) =>
  state.dashboard.detalleCurso;
/* -------------------------------------------------------------------------- */
/*                                    CUPOS                                   */
/* -------------------------------------------------------------------------- */
export const selectDetalleCupo = (state: RootState) =>
  state.dashboard.detalleCupo;

export const selectCuadraturaCupos = (state: RootState) =>
  state.dashboard.detalleCuadraturaCupos;

export const selectCuposTotales = (state: RootState) =>
  state.dashboard.cuposTotales;

export const selectCuposAnalizados = (state: RootState) =>
  state.dashboard.cuposAnalizados;
/* -------------------------------------------------------------------------- */
/*                                INSCRIPCIONES                               */
/* -------------------------------------------------------------------------- */
export const selectDesinscripcionesEnTransito = (state: RootState) =>
  state.dashboard.desinscripcionesEnTransito;
export const selectReservasEnTransito = (state: RootState) =>
  state.dashboard.reservasEnTransito;
/* -------------------------------------------------------------------------- */
/*                                 PARAMETROS                                 */
/* -------------------------------------------------------------------------- */
export const selectParametros = (state: RootState) =>
  state.dashboard.parametros;

export const selectEstadoDelSistema = (state: RootState) =>
  state.dashboard.parametros.find(({ clave }) => clave === "ESTADO_SISTEMA")
    ?.valor;

export const selectValidaciones = (state: RootState) =>
  state.dashboard.validaciones;
/* -------------------------------------------------------------------------- */
/*                                   QUEUES                                   */
/* -------------------------------------------------------------------------- */
export const selectQueuesData = (state: RootState) =>
  state.dashboard.queuesData;
/* -------------------------------------------------------------------------- */
/*                                   STATES                                   */
/* -------------------------------------------------------------------------- */
export const selectServicesVersions = (state: RootState) =>
  state.dashboard.servicesVersions;

export const selectEstadoTablasMirror = (state: RootState) =>
  state.dashboard.estadoTablasMirror;
/* -------------------------------------------------------------------------- */
/*                                   GENERAL                                  */
/* -------------------------------------------------------------------------- */
export const selectLoaderPage = (state: RootState) =>
  state.dashboard.loadingPageScreen;

export const selectSidebarOpen = (state: RootState) =>
  state.dashboard.sideBarOpen;

export const selectFiltrarNovedadesConErrores = (state: RootState) =>
  state.dashboard.filtrarNovedadesConErrores;

export const selectTokenValidationStatus = (state: RootState) =>
  state.dashboard.tokenValidationStatus;

export const {
  toggleSidebar,
  setLoadingPageScreen,
  setFiltrarNovedadesConErrores,
  setEstadoTablasMirror,
  patchEstadoTablaMirror,
  setCleanDetalleCurso,
  setCleanDetalleCupo,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
